import { BsMetaUtil, getLoanCollateralMarketUsdValue, getZcLedger, isLoanDefaulted } from "@bridgesplit/abf-react";
import { EmptyPlaceholder } from "@bridgesplit/ui";
import { ErrorOutlineRounded } from "@mui/icons-material";
import { TEST_IDS } from "app/constants";
import { getFeatureFlag } from "@bridgesplit/utils";

import { ActionProps } from "./types";
import { AppButton, BalanceDisplay } from "../../common";
import { TokenInputWithPrice } from "../../common/util";
import { LoanPositionStats } from "./LoanPositionStats";
import { useCollateralWithMaxWithdrawableAmounts, useWithdrawCollateral } from "./payments/hooks";

export default function CollateralWithdraw({ loanExpanded, loopPosition }: ActionProps) {
    const { collateralWithMaxWithdrawableAmounts } = useCollateralWithMaxWithdrawableAmounts(loanExpanded);

    const {
        inputAmount,
        setInputAmount,
        maxWithdrawable,
        collateralMetadata,
        supportedCollateralTokens,
        setCollateralToken,
        submit,
        ctaError,
        minRepayAmount
    } = useWithdrawCollateral({
        loanExpanded
    });

    if (!collateralWithMaxWithdrawableAmounts?.some((c) => c.amount > 0) && !isLoanDefaulted(loanExpanded))
        return (
            <EmptyPlaceholder
                icon={<ErrorOutlineRounded />}
                header="Withdraw not available"
                description={`Collateral can’t be withdrawn while your position is near liquidation. Repay ${
                    minRepayAmount
                        ? "at least " + BsMetaUtil.formatAmount(loanExpanded?.principalMetadata, minRepayAmount)
                        : "all or part of the loan"
                } to enable.`}
            />
        );

    const currentDebt = getZcLedger(loanExpanded)?.ledgerDebt.total ?? 0;
    const selectedCollateral = loanExpanded?.collateral.find((c) => c.metadata.mint === collateralMetadata?.mint);
    const currentCollateral = selectedCollateral?.amount ?? 0;
    const withdrawAmount = inputAmount ?? 0;
    const newCollateral = currentCollateral - withdrawAmount;
    const currentCollateralValueUsd = getLoanCollateralMarketUsdValue(loanExpanded);
    const newCollateralValueUsd = currentCollateralValueUsd - withdrawAmount * (selectedCollateral?.usdPrice ?? 0);
    const canSelectCollateral = getFeatureFlag("multiCollateral");

    return (
        <>
            <TokenInputWithPrice
                value={inputAmount}
                metadata={collateralMetadata}
                selectToken={
                    canSelectCollateral && supportedCollateralTokens && supportedCollateralTokens.length > 1
                        ? {
                              tokens: supportedCollateralTokens,
                              setToken: setCollateralToken,
                              header: "Select collateral"
                          }
                        : null
                }
                supportedDepositTokens={canSelectCollateral ? supportedCollateralTokens : undefined}
                label="Amount"
                setValue={setInputAmount}
                hideUsdPrice={false}
                belowInput={
                    <BalanceDisplay
                        isLoading={false}
                        balance={maxWithdrawable}
                        metadata={collateralMetadata}
                        setMax={() => setInputAmount(maxWithdrawable)}
                        insufficientBalance={false}
                        maxButtonCta="Max"
                        maxText="Available"
                    />
                }
            />

            <LoanPositionStats
                loanExpanded={loanExpanded}
                loopPosition={loopPosition}
                newPrincipalAmount={currentDebt}
                newCollateralAmount={newCollateral}
                newCollateralValueUsd={newCollateralValueUsd}
                isFullRepay={false}
                loading={false}
                show={{
                    debtAndCollateral: true,
                    health: true
                }}
            />

            <AppButton
                data-testid={TEST_IDS.WITHDRAW_COLLATERAL_BUTTON}
                isTransaction
                disabled={!!ctaError}
                asyncCta={{ onClickWithResult: submit }}
                fullWidth
            >
                {ctaError ?? "Withdraw"}
            </AppButton>
        </>
    );
}
